// src/App.jsx

import React from "react";
import { Routes, Route, Outlet, Navigate } from "react-router-dom";
import "./index.css";

/* ────────────────────────────────
   Dialog Provider
──────────────────────────────── */
import { DialogProvider } from "context/DialogContext";

/* ────────────────────────────────
   Layout & Core Pages
──────────────────────────────── */
import Layout from "components/Layout";
import ProtectedRoute from "components/ProtectedRoute";
import SessionExpired from "components/SessionExpired";
import Dashboard from "pages/Dashboard";
import Settings from "pages/Settings";
import UserProfile from "pages/Auth/UserProfile";
import AdminPanel from "pages/AdminPanel";

/* ────────────────────────────────
   Context
──────────────────────────────── */
import { useAppAccess } from "context/AppAccessContext";

/* ────────────────────────────────
   Auth Pages
──────────────────────────────── */
import Start from "pages/Auth/Start";
import SignupUser from "pages/Auth/SignupUser";
import UpdatePassword from "pages/Auth/UpdatePassword";
import ResetPassword from "pages/Auth/ResetPassword";
import MfaRequired from "pages/Auth/MfaRequired";
import Activate from "pages/Auth/Activate";
import SetPassword from "pages/Auth/SetPassword";

/* ────────────────────────────────
   App Modules
──────────────────────────────── */
import TradeTracker from "pages/TradeTracker";
import MarketMap from "pages/MarketMap";
import RecordRoom from "pages/RecordRoom";

/* ────────────────────────────────
   DebtStudio (with context)
──────────────────────────────── */
import { DebtStudioProvider } from "pages/DebtStudio/context/DebtStudioContext";

/* Portfolios session persistence */
import {
  PortfolioSessionProvider,
} from "pages/DebtStudio/context/PortfolioSessionContext";

/* Issues */
import DebtStudio from "pages/DebtStudio/components/Issues";
import ScaleBuilder from "pages/DebtStudio/components/Issues/ScaleBuilder";
import Reports from "pages/DebtStudio/components/Issues/Reports";

/* Portfolios */
import DebtStudioPortfolios from "pages/DebtStudio/components/Portfolios";

/* ────────────────────────────────
   FundPlanner
──────────────────────────────── */
import { StructurerProvider } from "pages/FundPlanner/context/StructurerContext";
import FundStructurer from "pages/FundPlanner/components/Structurer";
import Funds from "pages/FundPlanner/components/Funds";
import Portfolios from "pages/FundPlanner/components/Portfolios";

/* ────────────────────────────────
   CapitalFlow
──────────────────────────────── */
import {
  CapitalFlowOverview,
  CapitalFlowProjects,
  CapitalFlowClassifications,
  CapitalFlowDebtAllocations,
} from "pages/CapitalFlow";

/* ────────────────────────────────
   Knowledge Base Pages
──────────────────────────────── */
import GettingStarted from "pages/KnowledgeBase/GettingStarted";
import Learn from "pages/KnowledgeBase/Learn";
import TradeTrackerGuide from "pages/KnowledgeBase/TradeTrackerGuide";
import DebtStudioGuide from "pages/KnowledgeBase/DebtStudioGuide";
import FundPlannerGuide from "pages/KnowledgeBase/FundPlannerGuide";
import CapitalFlowGuide from "pages/KnowledgeBase/CapitalFlowGuide";
import FAQ from "pages/KnowledgeBase/FAQ";
import Support from "pages/KnowledgeBase/Support";

/* ────────────────────────────────
   Legal Pages
──────────────────────────────── */
import TermsPage from "pages/TermsPage";
import CompassLogoLab from "pages/CompassLogoLab";

export default function App() {
  const { apps } = useAppAccess();

  return (
    <DialogProvider>
      <Routes>
        {/* ─────────── Public / Auth Pages ─────────── */}
        <Route path="/" element={<Start />} index />
        <Route path="/start" element={<Start />} />
        <Route path="/signup-user" element={<SignupUser />} />
        <Route path="/update-password" element={<UpdatePassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/activate" element={<Activate />} />
        <Route path="/set-password" element={<SetPassword />} />
        <Route path="/session-expired" element={<SessionExpired />} />

        {/* ─────────── Legal Pages (public) ─────────── */}
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/kb/support" element={<Support />} />

        {/* ─────────── Private Routes (with Layout) ─────────── */}
        <Route
          element={
            <PortfolioSessionProvider>
              <Layout />
            </PortfolioSessionProvider>
          }
        >
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/mfa-required" element={<MfaRequired />} />
          <Route path="/user-profile" element={<UserProfile />} />
          <Route path="/settings" element={<Settings />} />
          <Route path="/admin" element={<AdminPanel />} />

          {process.env.NODE_ENV === "development" ? (
            <Route path="/dev/compass-logo" element={<CompassLogoLab />} />
          ) : null}

          {/* Free module */}
          <Route path="/market-map" element={<MarketMap />} />

          {/* ─────────── TradeTracker ─────────── */}
          <Route
            path="/trade-tracker"
            element={
              <ProtectedRoute
                apps={apps}
                requiredApp="trade-tracker"
                requireOrg={false}
              >
                <TradeTracker />
              </ProtectedRoute>
            }
          />

          {/* ─────────── RecordRoom ─────────── */}
          <Route
            path="/record-room"
            element={
              <ProtectedRoute
                apps={apps}
                requiredApp="record-room"
              >
                <RecordRoom />
              </ProtectedRoute>
            }
          />

          {/* ─────────── DebtStudio Section ─────────── */}
          <Route
            path="/debt-studio/*"
            element={
              <ProtectedRoute
                apps={apps}
                requiredApp="debt-studio"
              >
                <DebtStudioProvider>
                  <Outlet />
                </DebtStudioProvider>
              </ProtectedRoute>
            }
          >
            <Route index element={<Navigate to="issues" replace />} />

            <Route
              path="scale-builder"
              element={<Navigate to="issues/scale-builder" replace />}
            />

            <Route
              path="reports"
              element={<Navigate to="issues/reports" replace />}
            />

            <Route path="issues/*" element={<Outlet />}>
              <Route index element={<DebtStudio />} />
              <Route path="scale-builder" element={<ScaleBuilder />} />
              <Route path="reports" element={<Reports />} />
            </Route>

            <Route
              path="portfolios"
              element={<DebtStudioPortfolios />}
            />
          </Route>

          {/* ─────────── FundPlanner Section ─────────── */}
          <Route path="/fund-planner/*" element={<Outlet />}>
            <Route index element={<Navigate to="structurer" replace />} />

            <Route
              path="structurer"
              element={
                <ProtectedRoute
                  apps={apps}
                  requiredApps={["fund-planner", "debt-studio"]}
                >
                  <StructurerProvider>
                    <FundStructurer />
                  </StructurerProvider>
                </ProtectedRoute>
              }
            />

            <Route
              path="funds"
              element={
                <ProtectedRoute
                  apps={apps}
                  requiredApp="fund-planner"
                >
                  <Funds />
                </ProtectedRoute>
              }
            />

            <Route
              path="portfolios"
              element={
                <ProtectedRoute
                  apps={apps}
                  requiredApp="fund-planner"
                >
                  <Portfolios />
                </ProtectedRoute>
              }
            />
          </Route>

          {/* ─────────── CapitalFlow Section ─────────── */}
          <Route
            path="/capital-flow/*"
            element={
              <ProtectedRoute apps={apps} requiredApp="capital-flow">
                <Outlet />
              </ProtectedRoute>
            }
          >
            <Route index element={<Navigate to="overview" replace />} />
            <Route path="overview" element={<CapitalFlowOverview />} />
            <Route path="projects" element={<CapitalFlowProjects />} />
            <Route
              path="classifications"
              element={<CapitalFlowClassifications />}
            />
            <Route
              path="debt-allocations"
              element={<CapitalFlowDebtAllocations />}
            />
          </Route>

          {/* ─────────── Knowledge Base ─────────── */}
          <Route
            path="/kb/getting-started"
            element={<GettingStarted />}
          />

          <Route path="/kb/learn" element={<Learn />} />

          <Route path="/kb/debt-studio" element={<DebtStudioGuide />} />
          <Route path="/kb/fund-planner" element={<FundPlannerGuide />} />
          <Route path="/kb/capital-flow" element={<CapitalFlowGuide />} />
          <Route path="/kb/trade-tracker" element={<TradeTrackerGuide />} />

          <Route path="/kb/faq" element={<FAQ />} />
        </Route>
      </Routes>
    </DialogProvider>
  );
}
